/**
 * Iris watches along: one short, free note per studio step, read from the production's own
 * status. It says where the maker stands, what still blocks and what comes next — never guesses,
 * never spends. Asking Iris for more is a separate, explicit question.
 */
export type IrisNoteTone = "next" | "attention" | "working" | "done";

/** What a chip does: open a step, bring something into view, or run a studio action. */
export type IrisAction =
  | { step: string }
  | { focus: string }
  /** Open one shot of the storyboard (0-based position). */
  | { scene: number }
  | { run: "build-storyboard" | "rebuild-storyboard" | "approve-storyboard" | "images" | "preview" | "bokeh" | "portrait"
      | "prepare-song" | "outfits" | "backgrounds" | "animate" | "lipsync" | "create-production" | "preproduction" | "shorts"
      /** Release (#101): render the film (with its own confirmation), open the Cover tab, back to the productions. */
      | "render" | "cover" | "productions" }
  /** Answer a story-context question with one of its own options (free; rebuilds nothing by itself). */
  | { answer: { resolutionId: string; questionId: string; optionId: string } }
  /** Open Review with Iris over the storyboard. */
  | { review: true }
  /** Show only the shots at this review level in the storyboard. */
  | { filter: "attention" | "verify" }
  /** Ask Iris about this, in her own conversation. */
  | { ask: string }
  /** Try a stopped paid step again through the studio's own route, which confirms the price. */
  | { run: "retry"; kind: string }
  /** Iris steps aside: the maker directs from here (#99). */
  | { mode: "independent" }
  /** The maker has seen these mechanic repairs. */
  | { seen: string[] };

export interface IrisChip {
  label: string;
  action: IrisAction;
  primary?: boolean;
  /** What the step costs; 0 is free, null is priced in its own quote (#99). */
  priceUsd?: number | null;
}

/** The one next step Iris offers in guided mode: the note's primary chip, with its price (#99). */
export interface IrisProposal {
  label: string;
  action: IrisAction;
  priceUsd: number | null;
  /** What is left of this month's budget, so Iris can say so before a countdown (#99). */
  budgetLeftUsd?: number;
}

export interface IrisNote {
  /** Changes whenever the advice changes, so a dismissed note returns only when it is new. */
  key: string;
  tone: IrisNoteTone;
  text: string;
  /** Open another step, or bring an element on this page into view. */
  action?: { label: string; step?: string; focus?: string };
  /** Answers the maker can tap; agreeing moves the production on. */
  chips?: IrisChip[];
  /** The step waits for the maker (Iris opens on arrival in independent mode). */
  decision?: boolean;
  /** There is work to look at (Iris folds away on arrival in independent mode). */
  workView?: boolean;
  proposal?: IrisProposal;
}

/* The production payload is wide and loosely typed; Iris reads only what she needs. */
type Loose = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

const list = (value: unknown): Loose[] => (Array.isArray(value) ? (value as Loose[]) : []);
const count = (value: unknown): number =>
  Array.isArray(value) ? value.length : Number.isFinite(Number(value)) ? Number(value) : 0;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const names = (items: string[]) =>
  items.length <= 2 ? items.join(" and ") : `${items.slice(0, 2).join(", ")} and ${items.length - 2} more`;

const RUNNING = new Set(["queued", "running", "retrying", "cancel_requested"]);

function running(p: Loose, kinds: string[]): boolean {
  return list(p.jobs).some((job) => RUNNING.has(String(job.status)) && kinds.includes(String(job.kind)));
}

function note(tone: IrisNoteTone, text: string, action?: IrisNote["action"], extra: Partial<IrisNote> = {}): IrisNote {
  return { key: `${tone}:${text}`, tone, text, action, ...extra };
}

/** One picture's price; null when the image route has none yet. */
function unitCost(p: Loose): number | null {
  const unit = Number(p.providers?.image?.unit_cost);
  return unit > 0 ? unit : null;
}

const priced = (n: number, unit: number | null) => (unit === null ? null : Math.round(n * unit * 100) / 100);

/** The images job makes every missing and every outdated picture; its price is the server's quote for all of them. */
function imageQuote(p: Loose, fallback: number): { pictures: number; priceUsd: number | null } {
  const pictures = count(p.image_work_count) || fallback;
  const quote = Number(p.image_estimated_cost);
  return { pictures, priceUsd: quote > 0 ? Math.round(quote * 100) / 100 : priced(pictures, unitCost(p)) };
}

const picturesLabel = (verb: string, n: number) => `${verb} the ${n === 1 ? "picture" : `${n} pictures`}`;

function unfixedClothing(p: Loose): Loose[] {
  return list(p.outfits).filter((row: Loose) => !row?.from_look && row?.needs_pictures !== false && ["open", "draft"].includes(String(row?.state)));
}

/** A fresh production carries an empty analysis; the song is prepared once the server has aligned it. */
function songPrepared(p: Loose): boolean {
  if (p.status) return ["aligned", "storyboarded", "rendered"].includes(String(p.status));
  return !!p.analysis && Object.keys(p.analysis).length > 0;
}

// What the intake planned, in the words the maker used there (#100).
const PLANNED_WORDS: Record<string, string> = { characters: "characters", wardrobe: "clothing", companions: "props", locations: "places" };
const joinAll = (items: string[]) => (items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);

/** The intake's planned characters, clothing, props and places, offered with the price of their one quote. */
function preproductionNote(p: Loose): IrisNote | null {
  if (running(p, ["generate_preproduction"])) return note("working", "I'm making what we planned in the intake. It appears as drafts for your approval.");
  const offer: Loose | null = p.preproduction_offer || null;
  const words = list(offer?.kinds).map((kind: Loose) => PLANNED_WORDS[String(kind)]).filter((word): word is string => !!word);
  if (!offer?.ready || !words.length) return null;
  const what = joinAll(words);
  return note("next", `What we planned in the intake is ready to make: ${what}. The price comes first.`, undefined, {
    decision: true,
    chips: [{ label: `Make the planned ${what}`, action: { run: "preproduction" }, primary: true,
      priceUsd: Math.round(Number(offer.estimated_cost) * 100) / 100 }],
  });
}

function projectNote(p: Loose): IrisNote {
  if (running(p, ["generate_lyric_movie"])) return note("working", "I'm making your first cut. It appears here when it's ready.");
  if (running(p, ["analyze_audio", "align_lyrics", "prepare_storyboard"])) return note("working", "I'm preparing your song: lyrics, timing and tempo.");
  if (songPrepared(p) && preproductionNote(p)) return preproductionNote(p)!;
  if (songPrepared(p))
    return note("done", "Your song is ready. Next: who plays in the film.", { label: "Go to Cast & looks", step: "visual" }, {
      chips: [{ label: "On to the cast", action: { step: "visual" }, primary: true }],
    });
  return note("next", "First I prepare your song: lyrics, timing and tempo. Nothing is paid.", undefined, {
    decision: true,
    chips: [{ label: "Prepare the song", action: { run: "prepare-song" }, primary: true, priceUsd: 0 }],
  });
}

function isLead(asset: Loose): boolean {
  return /lead|artist|singer|vocal/i.test(String(asset.selection_role || asset.role || ""));
}

function castNote(p: Loose): IrisNote {
  const planned = preproductionNote(p);
  if (planned) return planned;
  const cast = list(p.production_assets).filter((asset) => asset.enabled !== false && asset.selection_enabled !== false);
  if (p.subject_preflight?.ready === false) {
    const blocking = Number(p.subject_preflight?.blocking_count || 0);
    return note(
      "attention",
      `The story names ${plural(blocking, "person or thing", "people or things")} I can't match to your cast yet. Decide who they are, then I can plan the shots.`,
      { label: "Show me", focus: "[data-subject-preflight-block]" },
    );
  }
  const gaps = list(p.cast_gaps_unanswered);
  if (gaps.length)
    return note("attention", `Your story mentions ${plural(gaps.length, "character")} who ${gaps.length === 1 ? "isn't" : "aren't"} in the cast yet. Add them or tell me they're extras.`);
  const waiting = cast.filter((asset) => asset.approval_status && asset.approval_status !== "approved");
  if (waiting.length)
    return note("attention", `${names(waiting.map((asset) => String(asset.name || "A cast member")))} still ${waiting.length === 1 ? "waits" : "wait"} for your approval before ${waiting.length === 1 ? "it" : "they"} can appear in the film.`);
  const people = cast.filter((asset) => String(asset.asset_type || "character") === "character" || asset.wardrobe_looks);
  const lead = people.find(isLead) || people[0];
  if (!lead) return note("next", "Choose who plays in this film — your lead first. I keep them identical in every shot.");
  if (!lead.wardrobe_look_id && list(lead.wardrobe_looks).length)
    return note("next", `${lead.name} has no look chosen yet. Pick one so the outfit stays the same in every shot.`);
  const unfixed = unfixedClothing(p);
  if (unfixed.length) {
    const who = names(unfixed.map((row: Loose) => String(row.name)));
    return note("next", `${who} ${unfixed.length === 1 ? "has" : "have"} no fixed clothing yet; I can suggest some (price first).`, undefined, {
      decision: true,
      chips: [{ label: "Suggest the clothing", action: { run: "outfits" }, primary: true, priceUsd: null }],
    });
  }
  const others = cast.filter((asset) => asset !== lead).map((asset) => String(asset.name || ""));
  return note(
    "done",
    `Cast is set: ${lead.name} leads${others.length ? `, with ${names(others)}` : ""}. Next: the places where the film happens.`,
    { label: "Go to Background", step: "background" },
    { chips: [{ label: "Yes, on to Background", action: { step: "background" }, primary: true }] },
  );
}

function vocalPending(p: Loose): boolean {
  const plan = p.vocal_moments;
  return !!plan && !plan.approved && !list(p.scenes).length;
}

function backgroundNote(p: Loose): IrisNote {
  const plan: Loose = p.background_plan || {};
  if (running(p, ["generate_background", "generate_preproduction"])) return note("working", "I'm making the background plates. You can stay here; they appear as drafts for your approval.");
  if (running(p, ["prepare_storyboard", "generate_storyboard", "analyze_audio"])) return note("working", "I'm preparing the shot plan. New places from the story will show up here to approve.");
  if (!plan.prepared) {
    const waiting = Number(plan.places_waiting || 0), approved = Number(plan.places_waiting_approved || 0);
    if (waiting && approved < waiting)
      return note("attention", `${approved} of ${plural(waiting, "place")} approved. Approve the rest so every shot knows where it is.`, undefined, { decision: true });
    return note(
      "next",
      vocalPending(p)
        ? `Your ${plural(Math.max(waiting, 1), "place")} ${waiting === 1 ? "is" : "are"} approved. Next I show you which lines are sung on camera, then I build the shot plan.`
        : `Your ${plural(Math.max(waiting, 1), "place")} ${waiting === 1 ? "is" : "are"} approved. Next: the shot plan, in Storyboard.`,
      { label: "Go to Storyboard", step: "storyboard" },
      { chips: [{ label: "On to the storyboard", action: { step: "storyboard" }, primary: true }] },
    );
  }
  const open = list(plan.locations).filter((location) => list(location.scene_ids).length && !location.approved);
  // A place without a plate needs one made first; a drafted plate only needs approving.
  const unplated = open.filter((location) => !location.reference_asset_id);
  if (unplated.length)
    return note("next", `${plural(unplated.length, "place")} ${unplated.length === 1 ? "has" : "have"} no background plate yet: ${names(unplated.map((location) => String(location.name || "a place")))}.`, undefined, {
      decision: true,
      chips: [{ label: unplated.length === 1 ? "Make the background plate" : `Make the ${unplated.length} background plates`,
        action: { run: "backgrounds" }, primary: true, priceUsd: null }],
    });
  if (open.length)
    return note("attention", `${plural(open.length, "place")} still ${open.length === 1 ? "needs" : "need"} an approved background: ${names(open.map((location) => String(location.name || "a place")))}.`, undefined, { decision: true });
  if (list(plan.unassigned_scene_ids).length)
    return note("attention", `${plural(list(plan.unassigned_scene_ids).length, "shot")} ${list(plan.unassigned_scene_ids).length === 1 ? "has" : "have"} no place yet. Give ${list(plan.unassigned_scene_ids).length === 1 ? "it" : "them"} one in the storyboard.`);
  return note("done", `All ${plural(count(plan.required), "background")} approved. Next: the storyboard.`, { label: "Go to Storyboard", step: "storyboard" }, { chips: [{ label: "On to the storyboard", action: { step: "storyboard" }, primary: true }] });
}

/** Sing with your photo: the photo version, then the price, then the film. */
function photoNote(p: Loose, scenes: Loose[]): IrisNote {
  if (running(p, ["prepare_singer_photo"])) return note("working", "I'm making the bokeh version of your photo. The person stays exactly the same.");
  if (running(p, ["generate_avatar_batch", "generate_avatar"])) return note("working", "Your photo is learning the song. The singing shots appear as they finish.");
  if (running(p, ["generate_singer_portrait"])) return note("working", "I'm making your singer's portrait.");
  const portrait: Loose = p.singer_photo || {};
  if (portrait.source && portrait.source !== "upload" && !portrait.approved) {
    const another = { label: list(portrait.candidates).length ? "Another one" : "Make the portrait", action: { run: "portrait" as const } };
    return list(portrait.candidates).length
      ? note("next", "Choose the portrait you like, or ask me for another one.", undefined, { decision: true, chips: [another] })
      : note("next", "First your singer's portrait. Each try is one picture; the price comes first.", undefined, {
          decision: true,
          chips: [{ ...another, primary: true }],
        });
  }
  // The server counts only clips made with the current photo and expression; older payloads fall back to any clip.
  const current: Loose = p.singer_photo || {};
  const singing = typeof current.singing_shots === "number" ? current.singing_shots : scenes.filter((scene) => scene.motion_asset_id).length;
  if (singing === scenes.length)
    return storyboardDone(p, "Your photo sings the whole song.", false);
  const photo: Loose = p.singer_photo || {};
  const quote = { label: "Show me the price", action: { focus: "[data-photo-quote]" } };
  const failed = list(photo.failed_shots);
  if (failed.length)
    return note("attention", `${plural(failed.length, "shot")} could not be made: ${String(failed[0]?.message || "")}`, undefined, {
      decision: true,
      chips: [{ ...quote, primary: true }],
    });
  if (!singing && !photo.has_bokeh && photo.choice !== "bokeh" && (photo.source || "upload") === "upload")
    return note("next", "Want a soft bokeh background behind you? Or use your photo as it is.", undefined, {
      decision: true,
      chips: [{ label: "Make a bokeh version", action: { run: "bokeh" }, primary: true }, { label: "Use my photo as it is", action: quote.action }],
    });
  const feeling = String(photo.expression_effective || "");
  const how = !feeling
    ? "Make them sing when you're ready — the price comes first."
    : (photo.expression || "auto") === "auto"
      ? `Your singer sings with a ${feeling} expression, to fit your song — you can change that. The price comes first.`
      : `Your singer sings with a ${feeling} expression, as you chose. The price comes first.`;
  return note("next", `Your film has ${plural(scenes.length, "shot")}. ${how}`, undefined, {
    decision: true,
    chips: [{ ...quote, primary: true }],
  });
}

function storyboardNote(p: Loose, selectedScene?: number): IrisNote {
  const scenes = list(p.scenes);
  if (scenes.length && (p.creative_brief as Loose | undefined)?.selected_production_option === "photo_session") return photoNote(p, scenes);
  // An open scene: Iris quietly offers help with it, without chips or pushing.
  if (scenes.length && selectedScene) return note("next", `Scene ${selectedScene} is open. Want to change something here? Ask me.`, undefined, { workView: true });
  if (running(p, ["prepare_storyboard", "generate_storyboard", "analyze_audio"]))
    return note("working", `I'm building your ${plural(Number(p.scene_count || scenes.length || 0), "shot")} plan. This takes a minute or two; nothing is paid.`);
  if (running(p, ["generate_images", "generate_motion", "generate_motion_batch", "generate_lipsync", "generate_lipsync_batch"]))
    return note("working", "I'm making pictures and clips for your shots. They appear in the strip as they finish.");
  if (!scenes.length) {
    const plan = p.vocal_moments;
    const moments = list(plan?.moments).length;
    if (plan && !plan.approved && moments)
      return note("next", `I picked ${plural(moments, "moment")} where your singer sings on camera. Keep them or leave some out, then build the storyboard.`, { label: "Show the moments", focus: '[aria-label="Vocal moments before the storyboard"]' }, {
        decision: true,
        workView: false,
        chips: [
          { label: "Yes, build the storyboard", action: { run: "build-storyboard" }, primary: true },
          { label: "Let me remove a few", action: { focus: '[aria-label="Vocal moments before the storyboard"]' } },
        ],
      });
    if (plan && !plan.approved)
      return note("next", "First choose which lines are sung on camera — I can pick them for you.", { label: "Show me", focus: '[aria-label="Vocal moments before the storyboard"]' }, { decision: true });
    return note("next", "Build the shot plan. You review every shot before any picture is paid for.", undefined, {
      decision: true,
      chips: [{ label: "Build the storyboard", action: { run: "build-storyboard" }, primary: true, priceUsd: 0 }],
    });
  }
  const conflicts = list(p.storyboard_validation?.conflicts);
  if (conflicts.length) {
    const position = Number(conflicts.find((conflict) => Number.isInteger(Number(conflict?.scene_position)))?.scene_position);
    return note("attention", `${plural(conflicts.length, "shot")} ${conflicts.length === 1 ? "needs" : "need"} a decision before pictures can be made: ${String(conflicts[0]?.message || "").slice(0, 140)}`, undefined, {
      decision: true,
      workView: true,
      ...(Number.isInteger(position) ? { chips: [{ label: `Open shot ${position + 1}`, action: { scene: position }, primary: true }] } : {}),
    });
  }
  if (String(p.storyboard_status) === "review")
    return note("next", `Read through the ${plural(scenes.length, "shot")} plan. When the story feels right, approve it — then pictures come next.`, undefined, {
      decision: true,
      workView: true,
      chips: [{ label: "Approve the storyboard", action: { run: "approve-storyboard" }, primary: true }],
    });
  const missing = count(p.missing_images), outdated = count(p.outdated_images);
  // New pictures invent clothes for a character without a fixed outfit: offer to fix it first.
  const unfixed = unfixedClothing(p);
  if (missing && unfixed.length) {
    const names = unfixed.map((row: Loose) => String(row.name));
    const who = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
    return note("next", `${who} ${names.length === 1 ? "has" : "have"} no fixed clothing yet; I can suggest some (price first).`, undefined, {
      decision: true,
      chips: [{ label: "Suggest the clothing", action: { run: "outfits" }, primary: true, priceUsd: null }],
    });
  }
  if (missing) {
    const quote = imageQuote(p, missing + outdated);
    return note("next", `${plural(missing, "shot")} still ${missing === 1 ? "needs" : "need"} a picture. Each batch shows its price before anything is made.`, undefined, {
      workView: true,
      chips: [{ label: picturesLabel("Make", quote.pictures), action: { run: "images" }, primary: true, priceUsd: quote.priceUsd }],
    });
  }
  if (outdated)
    return note("attention", `${plural(outdated, "picture")} ${outdated === 1 ? "is" : "are"} out of date because a cast member, look or place changed. Remake ${outdated === 1 ? "it" : "them"} when you're ready.`, undefined, {
      workView: true,
      chips: [((quote) => ({ label: picturesLabel("Remake", quote.pictures), action: { run: "images" as const }, primary: true,
        priceUsd: quote.priceUsd }))(imageQuote(p, outdated))],
    });
  // Music clip: the singing moments sing through Kling Avatar in one step, priced first.
  const singing: Loose | null = p.singing || null;
  if (singing) {
    const quote = { label: "Show me the price", action: { focus: "[data-singing-quote]" } };
    const failed = list(singing.failed_shots);
    if (failed.length)
      return note("attention", `${plural(failed.length, "singing moment")} could not be made: ${String(failed[0]?.message || "")}`, undefined, {
        decision: true,
        chips: [{ ...quote, primary: true }],
      });
    // A fine-tuned moment waits for the maker's price, not for its first song.
    const retuned = Object.values((singing.moment_settings || {}) as Record<string, Loose>).filter((m) => m?.tuned && m?.sung && !m?.current).length;
    if (retuned)
      return note("next", `${plural(retuned, "fine-tuned singing moment")} ${retuned === 1 ? "is" : "are"} not sung again yet. The price comes first.`, undefined, {
        decision: true,
        chips: [{ ...quote, primary: true }],
      });
    const waiting = count(singing.targets) - count(singing.singing_shots);
    if (waiting > 0)
      return note("next", `Your ${plural(waiting, "singing moment")} ${waiting === 1 ? "is" : "are"} ready to sing. The price comes first.`, undefined, {
        decision: true,
        chips: [{ ...quote, primary: true }],
      });
  }
  // Pictures made before the story's marks (soot, mud, rain …) can get them as a priced edit.
  const traces: Loose | null = p.traces_offer || null;
  if (traces && count(traces.frames) > 0)
    return note("next", `${plural(count(traces.frames), "picture")} ${count(traces.frames) === 1 ? "does not follow its" : "do not follow their"} scene yet${((parts) => (parts.length ? ` (${parts.join(", ")})` : ""))(["marks", "wind", "light"].filter((part) => list(traces.parts).map(String).includes(part)))}. The price comes first.`, undefined, {
      decision: true,
      chips: [{ label: "Show me the price", action: { focus: "[data-traces-offer]" }, primary: true }],
    });
  const motion = Number(p.missing_motion_count || 0);
  if (motion && String(p.production_type) === "motion_music_video")
    return note("next", `Every shot has a picture. ${plural(motion, "shot")} can still be animated.`, undefined, {
      workView: true,
      chips: [{ label: motion === 1 ? "Animate the shot" : `Animate the ${motion} shots`, action: { run: "animate" }, primary: true, priceUsd: null }],
    });
  const syncing = scenes.filter((scene) => scene.lipsync_candidate && scene.lipsync_source_asset_id && !scene.lipsync_ready).length;
  if (syncing)
    return note("next", `${plural(syncing, "singing shot")} can get lip-sync now. The price comes first.`, undefined, {
      decision: true,
      chips: [{ label: `Sync the ${plural(syncing, "singing shot")}`, action: { run: "lipsync" }, primary: true, priceUsd: null }],
    });
  return storyboardDone(p, "Your storyboard is complete.", true);
}

// A lyric video styles its words before Release, the same next step the storyboard's own button names.
function storyboardDone(p: Loose, done: string, workView: boolean): IrisNote {
  const lyrics = String(p.production_type || "lyric_video") === "lyric_video";
  const [label, step, next] = lyrics ? ["Lyrics", "lyrics", "Lyrics — style the words on screen"] : ["Release", "render", "Release"];
  return note("done", `${done} Next: ${next}.`, { label: `Go to ${label}`, step }, {
    ...(workView ? { workView: true } : {}),
    chips: [{ label: `Open ${label}`, action: { step }, primary: true }],
  });
}

// Release follows one order (#101): preview, film, cover, shorts — a shorts goal from the intake puts shorts first.
function releaseNote(outputs: string[], p: Loose): IrisNote {
  // Every render also saves a poster frame (render_thumbnail); only a cover from the Cover studio counts.
  const cover = outputs.includes("thumbnail"),
    short = outputs.includes("short");
  const shorts: IrisChip = { label: "Make your shorts", action: { run: "shorts" }, primary: true };
  const done = (text: string, chip: IrisChip) => note("done", text, undefined, { workView: true, chips: [chip] });
  if (p.export_settings?.release_goal === "shorts" && !short)
    return done("Your film is finished. Next: shorts for socials, cut from your film.", shorts);
  if (!cover) return done("Your film is finished. Next: a cover.", { label: "Make a cover", action: { run: "cover" }, primary: true });
  if (!short) return done("Next: shorts for socials, cut from your film.", shorts);
  return done("Your film, cover and shorts are ready. Download them below.", {
    label: "Back to your productions", action: { run: "productions" }, primary: true,
  });
}

function renderNote(p: Loose): IrisNote {
  if (running(p, ["render_preview", "render_final", "generate_lyric_movie"])) return note("working", "I'm rendering your film. You can leave this page; it keeps going.");
  const outputs = list(p.outputs).map((output) => String(output.kind || output.type || ""));
  // A finished film keeps its place in the release order even when later picture edits wait for a new render.
  if (outputs.includes("final")) return releaseNote(outputs, p);
  // Missing pictures are the usual blocker (the server sends render_ready=false only with blockers): lead back to them.
  const missing = p.missing_images !== undefined ? count(p.missing_images) : list(p.scenes).filter((scene) => !scene.image_asset_id).length;
  const back: IrisChip = { label: "Back to Storyboard", action: { step: "storyboard" }, primary: true };
  if (missing && (p.storyboard_assets_ready === false || !p.render_ready))
    return note("attention", `${plural(missing, "picture")} still to make before the film.`, undefined, { chips: [back] });
  const blockers = list(p.render_blockers).map(String);
  if (blockers.length) return note("attention", `Before the render: ${blockers[0]}${blockers.length > 1 ? ` (and ${plural(blockers.length - 1, "more thing")})` : ""}`);
  const silent = count(p.singing?.silent);
  if (p.render_ready && silent)
    return note("attention", `${plural(silent, "singing moment")} ${silent === 1 ? "is" : "are"} still silent: ${silent === 1 ? "it shows its" : "they show their"} still frame. Everything else is ready for a preview render.`, undefined, {
      chips: [{ label: "Make a preview render", action: { run: "preview" } }],
    });
  if (p.render_ready && outputs.includes("preview"))
    return note("next", "Your preview is below. Happy with it? Render the film.", undefined, {
      decision: true,
      chips: [{ label: "Render the film", action: { run: "render" }, primary: true }],
    });
  if (p.render_ready)
    return note("next", "Everything is ready. Start with a preview render; the final comes after you've watched it.", undefined, {
      decision: true,
      chips: [{ label: "Make a preview render", action: { run: "preview" }, primary: true }],
    });
  return note("attention", "A few shots are not ready yet. The checklist below shows which.", undefined, { chips: [back] });
}

/** Iris's note for the step the maker is on, or null where she stays quiet. */
export function irisWatchNote(project: unknown, step: string, selectedScene?: number): IrisNote | null {
  if (!project || typeof project !== "object") return null;
  const p = project as Loose;
  if (step === "render") return renderNote(p);
  const found = step === "project" ? projectNote(p) : step === "visual" ? castNote(p) : step === "background" ? backgroundNote(p)
    : step === "storyboard" ? storyboardNote(p, selectedScene) : null;
  // An open shot stays quiet on purpose: the maker is looking at it, and asks Iris from there.
  return found && withProposal(selectedScene && found.workView ? found : withWayOut(found), p);
}

// Guided mode never leaves the maker alone (#99): a note without its own way forward points at the step itself.
function withWayOut(found: IrisNote): IrisNote {
  const action = found.action as Loose | undefined;
  if (found.tone === "working" || (found.chips || []).length || action?.step || action?.focus) return found;
  return { ...found, chips: [{ label: "Show me where", action: { focus: "#lyricStage" }, primary: true }] };
}

/** After the treatment: Iris offers the handoff into the production; creating and preparing the song cost nothing (#99). */
export const INTAKE_HANDOFF_KEY = "intake-handoff";
export function intakeHandoffNote(): IrisNote {
  const label = "Create the production and prepare the song";
  const action: IrisAction = { run: "create-production" };
  return { ...note("next", "Your treatment is ready. Shall I create the production and prepare your song? Nothing is paid.", undefined, {
    decision: true,
    chips: [{ label, action, primary: true, priceUsd: 0 }],
    proposal: { label, action, priceUsd: 0 },
  }), key: INTAKE_HANDOFF_KEY };
}

// Steps Iris may start herself after the maker's yes; free ones cost 0, the rest carry their own quote (#99).
const FREE_RUNS = new Set(["prepare-song", "build-storyboard", "rebuild-storyboard", "approve-storyboard"]);

function withProposal(found: IrisNote, p: Loose): IrisNote {
  const chip = (found.chips || []).find((item) => item.primary && "run" in item.action);
  if (!chip || !("run" in chip.action)) return found;
  const priceUsd = chip.priceUsd !== undefined ? chip.priceUsd : FREE_RUNS.has(chip.action.run) ? 0 : null;
  const left = Number(p.budget?.remaining_usd);
  return { ...found, proposal: { label: chip.label, action: chip.action, priceUsd, ...(Number.isFinite(left) && p.budget ? { budgetLeftUsd: left } : {}) } };
}
